import React from 'react';
import { isFilledValue, sportFieldKey } from '../../utils/sport.js';

/**
 * One logged set: a label on the left, then one numeric input per metric.
 *
 * Inputs are `type="number"` with `inputMode="decimal"` so phones bring up the
 * number pad, and each turns green the moment it holds a number — the same
 * signal the card uses, only field by field.
 *
 * @param {string} label "set 1", or the activity name for one-step sports
 * @param {string} stationId
 * @param {number} setIndex
 * @param {{id: string, name: string}[]} metrics
 * @param {Record<string, string>} values
 * @param {Record<string, string>} [previousValues] last session's log, shown as
 *   placeholders so you can see what you managed last time
 * @param {(fieldKey: string, value: string) => void} onChange
 */
export default function SetRow({
  label,
  stationId,
  setIndex,
  metrics,
  values,
  previousValues = {},
  onChange,
}) {
  return (
    <div className="flex flex-col gap-1.5 border-b border-dashed border-line py-2 last:border-b-0">
      <span className="font-hand text-lg leading-none">{label}</span>

      <div className="flex flex-wrap gap-2">
        {metrics.map((metric) => {
          const fieldKey = sportFieldKey(stationId, setIndex, metric.id);
          const value = values[fieldKey] ?? '';
          const filled = isFilledValue(value);
          // What the same field held last session — a hint, never a value.
          const previous = previousValues[fieldKey];

          return (
            <label key={metric.id} className="flex min-w-[5.5rem] flex-1 flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-widest text-ink-soft">
                {metric.name}
              </span>
              <input
                type="number"
                inputMode="decimal"
                step="any"
                value={value}
                onChange={(event) => onChange(fieldKey, event.target.value)}
                placeholder={isFilledValue(previous) ? String(previous) : undefined}
                aria-label={`${label} ${metric.name}`}
                // box-hand gives the field uneven, hand-cut corners.
                className={`box-hand w-full px-2 py-1.5 text-center font-hand text-xl leading-none
                            placeholder:font-hand placeholder:text-ink-soft/60 ${
                              filled ? 'border-done bg-done-soft/50' : ''
                            }`}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
